import { useCallback } from 'react';
import { useTasksStore } from '../stores/tasksStore';
import { getCategoryRoutePrefix } from '../utils/relance/taskCategoryUtils';

/**
 * Returns a `getNextRoute` function that resolves the route of the next pending
 * task in the same section as the given task ID, or '/' if none remains.
 *
 * Pending = not already completed, not due today (daysSinceLastRelance !== 0).
 * Items are considered in ascending daysSinceLastRelance order (null last),
 * matching the sort order used by the task mapper.
 */
export function useNextTask() {
    const { dailyTasks, completedTaskIds } = useTasksStore();

    const getNextRoute = useCallback((currentId: string): string => {
        if (!dailyTasks) return '/';

        const section = dailyTasks.sections.find(s => s.items.some(i => i.id === currentId));
        if (!section) return '/';

        const sorted = [...section.items].sort((a, b) => {
            const aValue = a.daysSinceLastRelance ?? Infinity;
            const bValue = b.daysSinceLastRelance ?? Infinity;
            return aValue - bValue;
        });

        const nextTask = sorted.find(
            i => i.id !== currentId && !completedTaskIds.includes(i.id) && i.daysSinceLastRelance !== 0,
        );

        if (!nextTask) return '/';

        return `${getCategoryRoutePrefix(section.type)}/${nextTask.id}`;
    }, [dailyTasks, completedTaskIds]);

    return { getNextRoute };
}
